import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { FETCH_PORTFOLIO_REPORTS } from '../queries';
import { useTradingHub } from '../tradingHubContext';
import {
  type UsePortfolioListQueryParams,
  usePortfolioVariables,
} from './usePortfolioVars';

export function usePortfolioListQuery(params: UsePortfolioListQueryParams) {
  const { refreshKey } = useTradingHub();
  const { variables, isClientFilter } = usePortfolioVariables(params);

  const { error, data, previousData, refetch, loading } = useQuery(
    FETCH_PORTFOLIO_REPORTS,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
      variables,
      skip: !params.organizationId,
    },
  );

  useEffect(() => {
    if (refreshKey > 0) refetch();
  }, [refreshKey, refetch]);

  const pageData = data ?? previousData;

  const portfolios = useMemo(
    () =>
      (pageData?.fetchPortfolioReports?.portfolioReports || []).filter(Boolean),
    [pageData],
  );

  const pageGraphQLErrors = useMemo(() => {
    const errs = error?.graphQLErrors ?? [];
    return errs.map((e) => e.message);
  }, [error]);

  return {
    portfolios,
    totalCount: pageData?.fetchPortfolioReports?.totalCount ?? 0,
    loading,
    isPageReady: !loading && !error,
    fatalErrorMessage: error?.message,
    refetch,
    isClientFilter,
    pageGraphQLErrors,
  };
}
